"use strict"

import { aiLibInstance as API } from "@/plugins/hylable-sdk"
import { sleep } from "@hylable/common"
import { DEFAULT_MAX_SCORE, DEFAULT_HIGH_SCORE_THRESHOLD, DEFAULT_RUBRIC_ID } from "@/utils/skillRubric"

export function isNotFoundError(error) {
  const status = error?.response?.status ?? error?.status ?? error?.statusCode
  return status === 404
}

export function normalizeRubricsResponse(response) {
  if(Array.isArray(response)) return response
  if(Array.isArray(response?.rubrics)) return response.rubrics
  return []
}

const toNumber = (val) => {
  if(typeof val === "number") return val
  if(typeof val === "string" && val.trim() !== "" && !Number.isNaN(Number(val))) return Number(val)
  if(val && typeof val === "object") {
    if("value" in val && !Number.isNaN(Number(val.value))) return Number(val.value)
    if("score" in val && !Number.isNaN(Number(val.score))) return Number(val.score)
  }
  return null
}

export const parseSkillEstimation = (raw) => {
  if(!raw) return null
  if(typeof raw === "object") return raw
  try {
    return JSON.parse(raw)
  } catch (e) {
    console.error("Failed to parse skill estimation JSON:", e, raw)
    return null
  }
}

export const toSkillEstimation = (raw) => {
  const parsed = parseSkillEstimation(raw)
  if(!parsed) return null

  const criteriaDefinitions = parsed.definitions?.criteria || []
  const criteriaDefinitionMapByKey = Object.fromEntries(
    criteriaDefinitions
    .filter(d => d && typeof d === "object" && d.key)
    .map(d => [d.key, {
      definition: d.definition || null,
      maxScore: toNumber(d.maxScore) || DEFAULT_MAX_SCORE,
      highScoreThreshold: toNumber(d.highScoreThreshold),
    }])
  )

  const normalizeEvidenceArray = (value) => {
    if(Array.isArray(value)) return value
    return value ? [value] : []
  }

  const resolveCriterionDefinition = (criterion) => {
    if(!criterion || typeof criterion !== "object") return null
    return criteriaDefinitionMapByKey[criterion.key] || null
  }

  const mapParticipant = (participant, index) => {
    if(!participant || typeof participant !== "object") return null
    const name = participant.name || `参加者${index + 1}`
    const overallText = typeof participant.overall === "string" ? participant.overall : null
    const overall = (overallText == null)
      ? null
      : {label: "総合評価", score: null, evidences: [], text: overallText}

    const criteria = Array.isArray(participant.criteria) ? participant.criteria : []
    const evaluations = criteria
    .map((criterion, criterionIndex) => {
      if(!criterion || typeof criterion !== "object") return null
      const label = criterion.label || criterion.key || `基準${criterionIndex + 1}`
      const score = toNumber(criterion.score)
      const evidences = normalizeEvidenceArray(criterion.evidence)
      const criterionDefinition = resolveCriterionDefinition(criterion)
      const maxScore = criterionDefinition?.maxScore || DEFAULT_MAX_SCORE
      const highScoreThreshold = criterionDefinition?.highScoreThreshold ?? DEFAULT_HIGH_SCORE_THRESHOLD
      return {
        label,
        score,
        evidences,
        definition: criterionDefinition?.definition || null,
        maxScore,
        highScoreThreshold,
      }
    })
    .filter((evaluation) => evaluation && (evaluation.label || evaluation.score != null || evaluation.evidences.length))

    return {name, evaluations, overall}
  }

  const participants = parsed.result && Array.isArray(parsed.result.participants)
    ? parsed.result.participants
    : null
  if(!participants) return null

  return participants
  .map(mapParticipant)
  .filter((participant) => participant && (participant.evaluations.length || participant.overall))
}

export default {
  async getDiscussionSummary({oid, did}){
    return API.get(`/v1/${oid}/${did}`)
  },

  async createDiscussionSummary({oid, did}){
    return API.post(`/v1/${oid}/${did}`, {})
  },

  async deleteDiscussionSummary({oid, did}){
    return API.del(`/v1/${oid}/${did}`)
  },

  async askQuestion({oid, did}, question, history){
    return API.post(
      `/v1/${oid}/${did}/question`, {question, history}
    )
  },

  async getOrgSettings({oid}){
    const res = await API.get(`/v1/${oid}/settings`) || {}
    return {
      ...res,
      rubrics: normalizeRubricsResponse(res.rubrics),
    }
  },

  async updateOrgSettings({oid}, input){
    return API.put(`/v1/${oid}/settings`, input)
  },

  async updateCourseSettings({oid, cid}, input){
    return API.put(`/v1/${oid}/course/${cid}/settings`, input)
  },

  async getSkillEstimation({oid, did, rubricId = DEFAULT_RUBRIC_ID}){
    return API.get(`/v1/${oid}/${did}/skill_estimation/${rubricId}`)
  },

  async createSkillEstimation({oid, did, rubricId = DEFAULT_RUBRIC_ID}){
    return API.post(`/v1/${oid}/${did}/skill_estimation/${rubricId}`, {})
  },

  async getAsrState({ oid, did }) {
    return API.get(`/v1/${oid}/${did}/asr_batch`)
  },

  async requestAsr({ oid, did }) {
    return API.post(`/v1/${oid}/${did}/asr_batch`, undefined, undefined, {dummy: "1"})  // @@FIXME: [暫定対処] バックエンドのエラー回避のためダミーの query を付けてリクエスト
  },

  async getTextSummary({ oid, did }) {
    return API.get(`/v1/${oid}/${did}/text_summary/default`)
  },

  async createTextSummary({ oid, did }) {
    return API.post(`/v1/${oid}/${did}/text_summary/default`)
  },

  abortController: null,

  async pollingAsrState({ oid, did }, callback, limit=100) {
    this.abortController?.abort() // 既存のポーリングを中止
    const controller = new AbortController()
    const { signal } = controller
    this.abortController = controller

    const current = { asr: null, textSummary: null }

    const updateState = async (key, getStateFn, requestProcessFn) => {
      const isFirstTime = !current[key]
      if (!isFirstTime && current[key] !== "doing") return

      try {
        const res = await getStateFn({ oid, did })
        if (isFirstTime && ["none", "error"].includes(res.state)) {
          // 初回ループ時に「未実行」or「エラー」なら処理をリクエストする
          await requestProcessFn({ oid, did })
          res.state = "doing"
        }
        current[key] = res.state || "none"
      } catch(e) {
        // なにもしない。 次のループで再度取得を試みる。
      }
    }

    const updateAsrState = () => updateState("asr", this.getAsrState, this.requestAsr)
    const updateTextSummaryState = () => updateState("textSummary", this.getTextSummary, this.createTextSummary)

    for (let i = 0; i < limit; i++) {
      await updateAsrState()
      const isAsrCompleted = current.asr === "done"
      if (isAsrCompleted) await updateTextSummaryState()

      const state = isAsrCompleted ? current.textSummary : current.asr
      const stop = callback(state)

      if (stop || ![current.asr, current.textSummary].includes("doing") || signal.aborted) break
      await sleep(10 * 1000)
    }
  },
}
